/**
 * The API client.
 *
 * Two rules from the phase shape this file:
 *
 * 1. **The API is the source of truth for playback URLs.** Nothing here builds a `/content/<id>`
 *    string against a host we guessed; the content URL is always the API's own route, so moving
 *    the service is a config change, not a code change.
 * 2. **Verified facts and community claims arrive as separate objects** and stay separate here.
 *    The types below mirror that boundary so a component cannot accidentally render a community
 *    claim where a chain fact belongs.
 */

/** A chain or probe fact. Never user-editable, never presented as a community claim. */
export interface VerifiedFacts {
  declared_content_type: string;
  /** What the leading bytes are, by signature. Independent of what was declared. */
  detected_content_type?: string;
  /** What a player should expect after content-encoding is applied. */
  effective_content_type?: string;
  /** MIME name of the actual container — not a claim about the media inside it. */
  detected_container?: string;
  media_kind: string;
  content_sha256?: string;
  byte_length?: number;
  probe_status: string;
  /** Present only when a probe genuinely failed. Sanitised of local paths. */
  probe_error?: string;
  mismatch_class: string;
  probe?: {
    format_name?: string;
    codec_name?: string;
    duration_ms?: number;
    sample_rate?: number;
    channels?: number;
    bitrate?: number;
    stream_kinds?: string[];
  };
  midi?: {
    format: number;
    tracks: number;
    division: number;
    duration_ms?: number;
  };
}

export interface Proposal {
  value: string;
  annotation_id: string;
  status: 'verified' | 'curated' | 'community';
  confirmations: number;
  disputes: number;
  has_evidence: boolean;
}

export interface CommunityView {
  media_id: string;
  contribution_count: number;
  tallies?: Record<string, { value: string; votes: number }[]>;
  proposals?: Record<string, Proposal[]>;
}

export interface AudioRecord extends VerifiedFacts {
  id: string;
  block_height?: number;
  txid?: string;
  community: CommunityView;
  identification: {
    identification_score: number;
    has_community: boolean;
    has_curated: boolean;
  };
  /** Curated project memberships, by slug. An addition to the facts above, never an edit. */
  projects?: string[];
  playable: boolean;
}

/** How a search hit matched. The API says why, so a client can explain the result set. */
export type MatchKind =
  | 'inscription_id'
  | 'txid'
  | 'sha256'
  | 'block_height'
  | 'project'
  | 'id_prefix'
  | 'id_substring'
  | 'title';

/** A search hit carries the record plus the rule that produced it. */
export interface SearchHit extends AudioRecord {
  matched_by: MatchKind;
}

export interface Project {
  slug: string;
  title: string;
  short_description: string;
  long_description?: string;
  artwork_ref?: string;
  /** The project's own application inscription, scanned. Absent where the project has none. */
  application?: ProjectApplication;
  featured: boolean;
  display_order?: number;
  provenance: string;
  media: AudioRecord[];
  unresolved?: string[];
}

/** A project's application inscription, opened up. Mirrors `ProjectApplication` in the API. */
export interface ProjectApplication {
  inscription_id: string;
  content_type?: string;
  byte_length: number;
  /** False when the inscription could not be fetched or is not HTML — so an empty `media` list
   *  never has to be read as "we looked and found nothing" when in fact we could not look. */
  scanned: boolean;
  generates_at_runtime: boolean;
  /** Media declared as literal elements. Empty for an app that builds its media at runtime. */
  media?: { tag: string; src: string; declared_type?: string }[];
  /** Inscriptions the app loads, with the role each plays. */
  references?: { id: string; role: string }[];
}

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(path, { headers: { Accept: 'application/json' } });
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`${response.status} ${response.statusText}${detail ? `: ${detail.slice(0, 200)}` : ''}`);
  }
  return (await response.json()) as T;
}

export const api = {
  audio: (params: Record<string, string | number | boolean | undefined> = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) q.set(k, String(v));
    }
    return getJson<{ total: number; returned: number; offset: number; limit: number; records: AudioRecord[] }>(
      `/v1/audio?${q.toString()}`,
    );
  },
  record: (id: string) => getJson<AudioRecord>(`/v1/audio/${encodeURIComponent(id)}`),
  annotations: (id: string) => getJson<CommunityView>(`/v1/audio/${encodeURIComponent(id)}/annotations`),
  projects: () => getJson<{ projects: Project[] }>('/v1/projects'),
  project: (slug: string) => getJson<Project>(`/v1/projects/${encodeURIComponent(slug)}`),
  search: (q: string) =>
    getJson<{ query: string; exact: boolean; returned: number; records: SearchHit[] }>(
      `/v1/search?q=${encodeURIComponent(q)}`,
    ),
  identify: (limit = 50) =>
    getJson<{ returned: number; records: AudioRecord[] }>(`/v1/identify?limit=${limit}`),
  formats: () => getJson<Record<string, number>>('/v1/formats'),
  block: (height: number) =>
    getJson<{ height: number; audio_count: number; records: AudioRecord[] }>(`/v1/blocks/${height}`),
};

/**
 * The canonical content URL for an inscription.
 *
 * Deliberately relative and deliberately routed through the API: this is the one place a
 * playback URL is constructed, and it resolves through the catalog rather than pointing at a
 * third-party explorer.
 */
export function contentUrl(id: string): string {
  return `/v1/audio/${encodeURIComponent(id)}/content`;
}

/**
 * Content URL for any inscription, audio or not.
 *
 * Artwork is the reason this exists: a featured project's `artwork_ref` is an image inscription, so
 * the audio catalog cannot describe it and `contentUrl` cannot serve it. The route is the API's
 * general one, which verifies against a recorded digest when it has one and says `X-Verified: false`
 * when it does not.
 */
export function inscriptionContentUrl(id: string): string {
  return `/v1/content/${encodeURIComponent(id)}`;
}
